import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SEO from 'components/shared/SEO';
import MarketId from 'components/shared/MarketId';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import Column from 'components/shared/Column';
import { ORDER_TYPE } from 'constants/translations';
import { useClosedOrders, useIsMobile } from 'helpers/hooks';
import useMarkets from 'hooks/useMarkets';
import useAssets from 'hooks/useAssets';
import { formatCreatedAt } from 'helpers/dates';
import { getOptionsListOfAssets } from 'helpers/assets';
import { formatAmount } from '@nbx/frontend-helpers/format';
import { orderDataToStatus, getFillsQuantitySum } from 'helpers/orders';
import { useTranslation } from 'react-i18next';
import calculateAverageUnitPrice from 'helpers/calculateAverageUnitPrice';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const renderLink = order => `${microapps.routes.orders.path}/${order.id}`;

function OrdersClosed(props) {
  const { t } = useTranslation();
  const [asset, setAsset] = useState('All');
  const [viewOptions, setViewOptions] = useState({});
  const closedOrders = useClosedOrders(props.accountId, asset);
  const markets = useMarkets({ disabled: true });
  const assets = useAssets();
  const isMobile = useIsMobile();

  const columnWidths = isMobile ? [4, 4, 4] : [1.5, 2.5, 1.5, 1.5, 1.5, 1.5, 2];

  const endAlignIndex = isMobile ? 1 : 3;

  const headers = isMobile
    ? [t('common_market'), t('common_price'), t('orders_amount_status')]
    : [
        t('common_market'),
        t('common_objects_type'),
        t('common_amount'),
        t('orders_execution_price'),
        t('common_filled'),
        t('common_status'),
        t('common_date')
      ];

  useEffect(() => {
    setViewOptions({ All: t('orders_all_assets'), ...getOptionsListOfAssets(assets.data) });
  }, [assets.data, t]);

  const marketDecimals = {};
  if (!markets.loading && !assets.loading) {
    markets.data.forEach(market => {
      marketDecimals[market.pair] = {
        minTickLength: market.minTickLength,
        baseDecimalPrecisionDisplay: assets.data[market.baseAsset]?.decimalPrecisionDisplay ?? 2
      };
    });
  }

  const refetchOnError = () => {
    if (closedOrders.error) closedOrders.refetch();
    if (markets.error) markets.refetch();
    if (assets.error) assets.refetch();
  };

  const onChangeAsset = e => {
    setAsset(e.target.value);
  };

  const renderItem = (order, index) => {
    const [baseCurrency, quoteCurrency] = order.market.split('-');

    const executionPrice = calculateAverageUnitPrice(order);

    return {
      0: <MarketId pair={order.market} iconSize="20" />,
      1: (
        <Text monospaced>
          <Text
            styles={[
              styles.renderItemDetail,
              {
                [styles.buy]: order.side === 'BUY',
                [styles.sell]: order.side === 'SELL'
              }
            ]}>
            {order.side === 'BUY' ? t('common_buy').toUpperCase() : t('common_sell').toUpperCase()}
          </Text>{' '}
          {ORDER_TYPE(order.execution.type).toUpperCase()}{' '}
          {order.execution.type === 'LIMIT' ? `${order.execution.price} ${quoteCurrency}` : ''}
        </Text>
      ),
      2: (
        <Text monospaced>
          {formatAmount(order.quantity, marketDecimals[order.market].baseDecimalPrecisionDisplay)}{' '}
          {baseCurrency}
        </Text>
      ),
      3: (
        <Text monospaced>
          {executionPrice
            ? `${formatAmount(
                calculateAverageUnitPrice(order),
                marketDecimals[order.market].minTickLength
              )} ${quoteCurrency}`
            : '-'}
        </Text>
      ),
      4: (
        <Text monospaced>
          {formatAmount(getFillsQuantitySum(order), marketDecimals[order.market].baseDecimalPrecisionDisplay)}{' '}
          {baseCurrency}
        </Text>
      ),
      5: <Text>{orderDataToStatus(order)}</Text>,
      6: <Text monospaced>{formatCreatedAt(order.events.createdAt)}</Text>
    }[index];
  };

  const renderItemMobile = (order, index) => {
    const executionPrice = calculateAverageUnitPrice(order);

    return {
      0: (
        <Column>
          <MarketId pair={order.market} iconSize="20" />
          <span>
            <Text
              styles={[
                styles.renderItemDetail,
                {
                  [styles.buy]: order.side === 'BUY',
                  [styles.sell]: order.side === 'SELL'
                }
              ]}>
              {order.side === 'BUY' ? t('common_buy').toUpperCase() : t('common_sell').toUpperCase()}
            </Text>{' '}
            <Text styles={[styles.renderItemDetail]}>{ORDER_TYPE(order.execution.type).toUpperCase()}</Text>
          </span>
          <Text styles={[styles.renderItemDetail]} monospaced>
            {order.execution.type === 'LIMIT' ? `${order.execution.price}` : ''}
          </Text>
        </Column>
      ),
      1: (
        <Column endAligned>
          <Text monospaced>
            {executionPrice
              ? formatAmount(calculateAverageUnitPrice(order), marketDecimals[order.market].minTickLength)
              : '-'}
          </Text>
          <Text styles={[styles.renderItemDetail]}>&nbsp;</Text>
        </Column>
      ),
      2: (
        <Column endAligned>
          <Text monospaced>
            {formatAmount(order.quantity, marketDecimals[order.market].baseDecimalPrecisionDisplay)}
          </Text>
          <Text styles={[styles.renderItemDetail]} muted>
            {orderDataToStatus(order)}
          </Text>
        </Column>
      )
    }[index];
  };

  const loading = closedOrders.loading || markets.loading || assets.loading;

  return (
    <>
      <SEO title={t('orders_closed_orders')} />
      <Table
        columnWidths={columnWidths}
        data={closedOrders.data}
        keyField="id"
        hasNextPage={closedOrders.hasNextPage && !closedOrders.pausedForEmptyPages}
        pausedForEmptyPages={closedOrders.pausedForEmptyPages}
        error={closedOrders.error}
        refetchData={refetchOnError}
        isLoading={loading}
        fetchNextPage={closedOrders.fetchNextPage}
        fetchAllPages={closedOrders.fetchAllPages}
        isLoadingNextPage={closedOrders.loadingNextPage}
        isLoadingAllPages={closedOrders.loadingAllPages}
        emptyHeading={t('market_you_dont_have_closed')}
        emptyText={t('market_once_filled')}
        endAlignIndex={endAlignIndex}
        view={asset}
        viewOptions={viewOptions}
        onViewChange={onChangeAsset}
        headers={headers}
        renderItem={isMobile ? renderItemMobile : renderItem}
        renderLink={renderLink}
        rowHeight={isMobile ? 70 : 55}
        rowStyles={[styles.orderRow]}
        borders={!isMobile}
      />
    </>
  );
}

OrdersClosed.propTypes = {
  accountId: PropTypes.string.isRequired
};

export default OrdersClosed;
